import { useCallback } from 'react';
import { FormFieldState } from '../../../../core/components';
import { Channel } from '../../types/channel';
import { ChannelFormValues, channelFormSchema } from '../../types/channelForm';
import { useTouchedFormState } from '../useTouchedFormState/useTouchedFormState';

export type ChannelFieldName = 'branchId' | 'themeId';

const INITIAL_VALUES: ChannelFormValues = { channel: '', branchId: '', themeId: '' };

type UseChannelFormResult = {
  values: ChannelFormValues;
  isValid: boolean;
  handleChannelSelect: (channel: Channel) => void;
  handleBranchSelect: (branchId: string) => void;
  handleThemeSelect: (themeId: string) => void;
  handleBlur: (field: ChannelFieldName) => void;
  getFieldState: (field: ChannelFieldName) => FormFieldState;
  getFieldError: (field: ChannelFieldName) => string | undefined;
};

function isBranchSkippedForOnlineChannel(
  field: keyof ChannelFormValues,
  values: ChannelFormValues,
): boolean {
  return field === 'branchId' && values.channel === 'loja_online';
}

export function useChannelForm(): UseChannelFormResult {
  const { values, setValues, isValid, handleBlur, getFieldState, getFieldError } =
    useTouchedFormState<ChannelFormValues>({
      schema: channelFormSchema,
      initialValues: INITIAL_VALUES,
      isFieldSkipped: isBranchSkippedForOnlineChannel,
    });

  const handleChannelSelect = useCallback(
    (channel: Channel) => {
      setValues((previous) => ({
        ...previous,
        channel,
        branchId: channel === 'loja_online' ? '' : previous.branchId,
      }));
    },
    [setValues],
  );

  const handleBranchSelect = useCallback(
    (branchId: string) => {
      setValues((previous) => ({ ...previous, branchId }));
    },
    [setValues],
  );

  const handleThemeSelect = useCallback(
    (themeId: string) => {
      setValues((previous) => ({ ...previous, themeId }));
    },
    [setValues],
  );

  return {
    values,
    isValid,
    handleChannelSelect,
    handleBranchSelect,
    handleThemeSelect,
    handleBlur,
    getFieldState,
    getFieldError,
  };
}
